// Classifies a failed admin members/roster query so the section can render an
// honest state: a deterministic client error (leader-gated 403, or a 404 for a
// group the viewer can't see) is not a transient outage — retrying never helps,
// so the section shows an accurate message with no "Try again". A 5xx / network
// failure keeps the retry affordance. Shared by the Circle and Network members
// sections, whose roster query (`fetchGroupRoster`) throws on any non-2xx.

import { isNotFoundApiError, failureFromError } from '../../../lib/api-error';

// The roster/members endpoints return these codes for a deterministic deny.
// `http_403` covers a bodyless 403 (the code falls back to `http_<status>`
// when the envelope is absent — see `throwApiError`); a bodyless 404 is
// already a typed `NotFoundApiError`, caught below. We list only the
// genuinely non-retryable statuses: NOT all 4xx, because 401 (re-auth),
// 408 (timeout) and 429 (rate limit) ARE retryable — classifying them as
// "no permission, no retry" would strand a leader who can access the page
// behind a false permanent-denial message.
const ACCESS_CODES: ReadonlySet<string> = new Set([
  'POLICY_DENIED',
  'FORBIDDEN',
  'NOT_FOUND',
  'http_403',
]);

/**
 * True when the error is a deterministic client-side denial (403/404) for
 * which retrying is pointless — the section should present an accurate,
 * retry-free message rather than the generic "something didn't respond".
 * Retryable client errors (401/408/429) and 5xx keep the retry affordance.
 */
export function isRosterAccessError(err: unknown): boolean {
  if (isNotFoundApiError(err)) return true;
  const { code } = failureFromError(err, '');
  return ACCESS_CODES.has(code);
}
